import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MDRBadge } from "@/components/MDRBadge";
import { CsvIOButtons } from "@/components/admin/CsvIOButtons";
import { toast } from "sonner";
import {
  medicalDeviceModelsData, medicalDeviceKitsData, pharmaciesData
} from "@/data/platformData";
import { Cpu, Plus, Pencil, Trash2, Package, Link as LinkIcon, Building2 } from "lucide-react";

const STATUS_STYLE = "bg-emerald-50 text-emerald-700 border-emerald-200";

const deviceCols = [
  { key: "id", label: "id" }, { key: "name", label: "modello" }, { key: "brand", label: "marca" },
  { key: "type", label: "tipologia" }, { key: "profile", label: "profile" }, { key: "certification", label: "certificazione" }
];
const kitCols = [
  { key: "id", label: "id" }, { key: "name", label: "nome" }, { key: "description", label: "descrizione" },
  { key: "deviceModelIds", label: "device_model_ids", get: (r) => r.deviceModelIds.join("|"), parse: (v) => v.split("|").map(s => s.trim()).filter(Boolean) }
];

const emptyDevice = { name: "", brand: "", type: "", profile: "", certification: "MDR Classe IIa" };
const emptyKit = { name: "", description: "", deviceModelIds: [] };

export const AdminDevicesPage = () => {
  const [devices, setDevices] = useState(medicalDeviceModelsData);
  const [kits, setKits] = useState(medicalDeviceKitsData);
  const [pharmacies, setPharmacies] = useState(pharmaciesData);

  const [editingDev, setEditingDev] = useState(null);
  const [openDev, setOpenDev] = useState(false);
  const [editingKit, setEditingKit] = useState(null);
  const [openKit, setOpenKit] = useState(false);

  // Device CRUD
  const startDev = (d) => { setEditingDev(d ? { ...d } : { ...emptyDevice, id: `dm${Date.now()}` }); setOpenDev(true); };
  const saveDev = () => {
    if (!editingDev.name) { toast.error("Inserire nome modello"); return; }
    setDevices(prev => {
      const ex = prev.find(x => x.id === editingDev.id);
      return ex ? prev.map(x => x.id === editingDev.id ? editingDev : x) : [...prev, editingDev];
    });
    setOpenDev(false);
    toast.success("Elettromedicale salvato");
  };
  const removeDev = (id) => { setDevices(prev => prev.filter(x => x.id !== id)); toast.success("Elettromedicale rimosso"); };

  // Kit CRUD
  const startKit = (k) => { setEditingKit(k ? { ...k, deviceModelIds: [...k.deviceModelIds] } : { ...emptyKit, id: `kit${Date.now()}` }); setOpenKit(true); };
  const saveKit = () => {
    if (!editingKit.name) { toast.error("Inserire nome kit"); return; }
    setKits(prev => {
      const ex = prev.find(x => x.id === editingKit.id);
      return ex ? prev.map(x => x.id === editingKit.id ? editingKit : x) : [...prev, editingKit];
    });
    setOpenKit(false);
    toast.success("Kit salvato");
  };
  const removeKit = (id) => { setKits(prev => prev.filter(x => x.id !== id)); toast.success("Kit rimosso"); };
  const toggleKitDevice = (devId) => {
    setEditingKit(k => ({ ...k, deviceModelIds: k.deviceModelIds.includes(devId) ? k.deviceModelIds.filter(x => x !== devId) : [...k.deviceModelIds, devId] }));
  };

  // Pharmacy <-> Kit assignment
  const togglePharmacyKit = (phId, kitId) => {
    setPharmacies(prev => prev.map(ph => {
      if (ph.id !== phId) return ph;
      const cur = ph.kitIds || [];
      return { ...ph, kitIds: cur.includes(kitId) ? cur.filter(x => x !== kitId) : [...cur, kitId] };
    }));
  };
  const savePharmacyKits = () => toast.success("Associazione kit ↔ farmacia aggiornata");

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Cpu className="h-4 w-4 text-slate-600" />
          <span className="text-xs uppercase tracking-wider text-muted-foreground">3 · Kit elettromedicali</span>
          <MDRBadge variant="admin" size="sm" />
        </div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Censimento elettromedicali e kit</h1>
        <p className="text-sm text-muted-foreground mt-1">Modelli certificati, composizione kit, distribuzione farmacie</p>
      </div>

      <Tabs defaultValue="devices" className="space-y-4">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="devices" data-testid="devices-tab-devices">Elettromedicali</TabsTrigger>
          <TabsTrigger value="kits" data-testid="devices-tab-kits">Kit</TabsTrigger>
          <TabsTrigger value="kit-devices" data-testid="devices-tab-kit-devices">Device ↔ Kit</TabsTrigger>
          <TabsTrigger value="kit-pharmacy" data-testid="devices-tab-kit-pharmacy">Kit ↔ Farmacia</TabsTrigger>
        </TabsList>

        {/* Devices census */}
        <TabsContent value="devices" className="space-y-3">
          <div className="flex flex-wrap items-center justify-end gap-2">
            <CsvIOButtons rows={devices} columns={deviceCols} filename="elettromedicali" onImport={(p) => setDevices(p.map(x => ({ ...emptyDevice, ...x })))} testIdPrefix="devices-csv" />
            <Button variant="premium" onClick={() => startDev(null)} className="gap-2" data-testid="devices-new-btn">
              <Plus className="h-4 w-4" /> Nuovo modello
            </Button>
          </div>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {devices.map(d => (
              <Card key={d.id} data-testid={`device-card-${d.id}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">{d.name}</CardTitle>
                  <p className="text-xs text-muted-foreground">{d.brand} · {d.type}</p>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Badge variant="outline" className={STATUS_STYLE}>{d.certification}</Badge>
                  <p className="text-[11px] text-muted-foreground">Profilo: {d.profile}</p>
                  <div className="flex justify-end gap-1 pt-1 border-t">
                    <Button variant="ghost" size="icon" onClick={() => startDev(d)} data-testid={`device-edit-${d.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="text-destructive" onClick={() => removeDev(d.id)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Kits census */}
        <TabsContent value="kits" className="space-y-3">
          <div className="flex flex-wrap items-center justify-end gap-2">
            <CsvIOButtons rows={kits} columns={kitCols} filename="kit" onImport={(p) => setKits(p.map(x => ({ ...emptyKit, ...x, deviceModelIds: Array.isArray(x.deviceModelIds) ? x.deviceModelIds : [] })))} testIdPrefix="kits-csv" />
            <Button variant="premium" onClick={() => startKit(null)} className="gap-2" data-testid="kits-new-btn">
              <Plus className="h-4 w-4" /> Nuovo kit
            </Button>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            {kits.map(k => (
              <Card key={k.id} data-testid={`kit-card-${k.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start gap-2">
                    <Package className="h-4 w-4 text-slate-600 mt-0.5" />
                    <div>
                      <CardTitle className="text-base">{k.name}</CardTitle>
                      <p className="text-xs text-muted-foreground">{k.description}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex flex-wrap gap-1">
                    {k.deviceModelIds.map(did => {
                      const d = devices.find(x => x.id === did);
                      return d ? <Badge key={did} variant="secondary" className="text-[10px]">{d.type}</Badge> : null;
                    })}
                  </div>
                  <div className="flex justify-end gap-1 pt-1 border-t">
                    <Button variant="ghost" size="icon" onClick={() => startKit(k)} data-testid={`kit-edit-${k.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="text-destructive" onClick={() => removeKit(k.id)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Device ↔ Kit matrix (read-only summary) */}
        <TabsContent value="kit-devices" className="space-y-3">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><LinkIcon className="h-4 w-4 text-slate-600" /> Composizione dei kit</CardTitle>
              <p className="text-xs text-muted-foreground">Modifica composizione dal tab Kit ▸ Modifica</p>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2 font-medium">Kit</th>
                      {devices.map(d => <th key={d.id} className="text-center p-2 font-medium text-[11px]" title={d.name}>{d.type}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {kits.map(k => (
                      <tr key={k.id} className="border-b">
                        <td className="p-2 font-medium">{k.name}</td>
                        {devices.map(d => (
                          <td key={d.id} className="text-center p-2">
                            {k.deviceModelIds.includes(d.id) ? <span className="text-emerald-600">●</span> : <span className="text-muted-foreground/30">○</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Kit ↔ Pharmacy matrix */}
        <TabsContent value="kit-pharmacy" className="space-y-3">
          <div className="flex justify-end">
            <Button variant="premium" size="sm" onClick={savePharmacyKits} className="gap-2" data-testid="kit-pharmacy-save-btn">Salva associazioni</Button>
          </div>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Building2 className="h-4 w-4 text-slate-600" /> Distribuzione kit per farmacia</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2 font-medium">Farmacia</th>
                      {kits.map(k => <th key={k.id} className="text-center p-2 font-medium text-[11px]">{k.name}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {pharmacies.map(ph => (
                      <tr key={ph.id} className="border-b" data-testid={`kit-pharmacy-row-${ph.id}`}>
                        <td className="p-2 font-medium">{ph.name}</td>
                        {kits.map(k => {
                          const ck = (ph.kitIds || []).includes(k.id);
                          return (
                            <td key={k.id} className="text-center p-2">
                              <Switch checked={ck} onCheckedChange={() => togglePharmacyKit(ph.id, k.id)} data-testid={`kit-pharmacy-toggle-${ph.id}-${k.id}`} />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Device dialog */}
      <Dialog open={openDev} onOpenChange={setOpenDev}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingDev && devices.find(p => p.id === editingDev.id) ? "Modifica modello" : "Nuovo modello"}</DialogTitle>
          </DialogHeader>
          {editingDev && (
            <div className="space-y-3">
              <div><Label>Nome modello</Label><Input value={editingDev.name} onChange={(e) => setEditingDev({ ...editingDev, name: e.target.value })} data-testid="device-form-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Marca</Label><Input value={editingDev.brand} onChange={(e) => setEditingDev({ ...editingDev, brand: e.target.value })} /></div>
                <div><Label>Tipologia</Label><Input value={editingDev.type} onChange={(e) => setEditingDev({ ...editingDev, type: e.target.value })} placeholder="ECG, Spirometro..." /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Profile (servizio)</Label><Input value={editingDev.profile} onChange={(e) => setEditingDev({ ...editingDev, profile: e.target.value })} placeholder="ecg, glicemia..." /></div>
                <div><Label>Certificazione</Label><Input value={editingDev.certification} onChange={(e) => setEditingDev({ ...editingDev, certification: e.target.value })} /></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDev(false)}>Annulla</Button>
            <Button onClick={saveDev} data-testid="device-form-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Kit dialog */}
      <Dialog open={openKit} onOpenChange={setOpenKit}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingKit && kits.find(p => p.id === editingKit.id) ? "Modifica kit" : "Nuovo kit"}</DialogTitle>
          </DialogHeader>
          {editingKit && (
            <div className="space-y-3">
              <div><Label>Nome</Label><Input value={editingKit.name} onChange={(e) => setEditingKit({ ...editingKit, name: e.target.value })} data-testid="kit-form-name" /></div>
              <div><Label>Descrizione</Label><Input value={editingKit.description} onChange={(e) => setEditingKit({ ...editingKit, description: e.target.value })} /></div>
              <div>
                <Label>Elettromedicali inclusi</Label>
                <div className="grid grid-cols-1 gap-1 mt-1 max-h-56 overflow-y-auto p-2 rounded-md border">
                  {devices.map(d => (
                    <label key={d.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox checked={editingKit.deviceModelIds.includes(d.id)} onCheckedChange={() => toggleKitDevice(d.id)} />
                      <span className="truncate">{d.name} <span className="text-muted-foreground">({d.type})</span></span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenKit(false)}>Annulla</Button>
            <Button onClick={saveKit} data-testid="kit-form-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDevicesPage;
